"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createReceivableAdvanceRateMiscAvailableFieldProposals = exports.createReceivableAdvanceRateEvaluator = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const advanceRateValidations_1 = require("./advanceRateValidations");
const sanitizeConcentrationLimitReceivable = (input) => {
    const { baseFields, dynamicIntermediateFields, requiredIntermediateFields, postProcessedFields, valuationFields, eligibilityFields, concentrationLimitFields, } = input;
    // NOTE: this is not guaranteed to exist for every receivable, formulas MUST be authored
    // very defensively (use `IFERROR`) if referencing a concentrationLimitWaivers property
    const concentrationLimitWaivers = input.concentrationLimitWaivers.reduce((acc, waiver) => {
        // eslint-disable-next-line no-param-reassign
        acc[`${waiver.propertyKey}.${waiver.waiveReason}`] =
            waiver.waivedAmount ?? 0;
        return acc;
    }, {});
    return {
        baseFields,
        dynamicIntermediateFields,
        requiredIntermediateFields,
        postProcessedFields,
        valuationFields,
        eligibilityFields,
        concentrationLimitFields,
        concentrationLimitWaivers,
    };
};
// goes through a modifier tree and returns a "modified" advance rate if applicable
const applyModifierTree = (modifierNode, formulaEngine) => {
    let modifiedAdvanceRate = null;
    const path = [modifierNode.name];
    // traverse modifier tree for first child that is TRUE
    for (const child of modifierNode.children) {
        if (formulaEngine.evaluateBoolean(child.isMatch.formula)) {
            const result = applyModifierTree(child, formulaEngine);
            modifiedAdvanceRate = result.modifiedAdvanceRate;
            path.push(...result.path);
            break;
        }
    }
    // otherwise, return modified advance rate for current node
    if (modifiedAdvanceRate === null) {
        modifiedAdvanceRate = formulaEngine.evaluateNumber(modifierNode.modifierFormula.formula);
    }
    (0, common_1.finleyAssert)(typeof modifiedAdvanceRate === 'number' &&
        !Number.isNaN(modifiedAdvanceRate), `Modifier formula must evaluate to a number. Got: ${modifiedAdvanceRate}`);
    return { modifiedAdvanceRate, path };
};
const getAdvanceRate = ({ advanceRateNode, modifiers, variables, formulaEngine, stringFields, }) => {
    let advanceRate = null;
    const path = [advanceRateNode.name];
    const modifierPath = [];
    // if children exist, traverse the first child where isMatch is TRUE
    for (const child of advanceRateNode.children) {
        if (formulaEngine.evaluateBoolean(child.isMatch.formula)) {
            const result = getAdvanceRate({
                advanceRateNode: child,
                modifiers,
                variables,
                formulaEngine,
                stringFields,
            });
            advanceRate = result.advanceRate;
            path.push(...result.path);
            modifierPath.push(...result.modifierPath);
            break;
        }
    }
    // if no children exist OR all children's isMatch resolves to FALSE, return the formula of current node
    if (advanceRate === null) {
        advanceRate = formulaEngine.evaluateNumber(advanceRateNode.advanceRate.formula);
        (0, common_1.finleyAssert)(typeof advanceRate === 'number' && !Number.isNaN(advanceRate), `Advance rate formula must evaluate to a number. Got: ${advanceRate}`);
    }
    // pass in advance rate to every modifier tree in order
    for (const modifierName of advanceRateNode.modifierNames) {
        const modifier = modifiers.find((m) => m.name === modifierName);
        (0, common_1.finleyAssert)(modifier, `Could not find a modifier tree with the name: ${modifierName}`);
        if (formulaEngine.evaluateBoolean(modifier.isMatch.formula)) {
            // pass in current advance rate as a formula input variable
            const variablesWithAdvanceRate = { ...variables, advanceRate };
            formulaEngine.setVariables(variablesWithAdvanceRate, {
                flatten: true,
                fixupStringVariables: stringFields,
            });
            // update advance rate to output of modifier tree
            const modifierResult = applyModifierTree(modifier, formulaEngine);
            advanceRate = modifierResult.modifiedAdvanceRate;
            modifierPath.push(modifierResult.path.join('#'));
        }
    }
    return { advanceRate, path, modifierPath };
};
const getAllFormulas = (params) => {
    const formulas = [];
    if (params.advanceRateNode) {
        formulas.push(params.advanceRateNode.advanceRate.formula);
        formulas.push(params.advanceRateNode.isMatch.formula);
        for (const child of params.advanceRateNode.children) {
            formulas.push(...getAllFormulas({ advanceRateNode: child }));
        }
    }
    else {
        formulas.push(params.modifierNode.modifierFormula.formula);
        formulas.push(params.modifierNode.isMatch.formula);
        for (const child of params.modifierNode.children) {
            formulas.push(...getAllFormulas({ modifierNode: child }));
        }
    }
    return formulas;
};
const advanceRateTreeEvaluator = (root, modifiers, stringFields, f1EngineMode) => {
    const formulas = getAllFormulas({ advanceRateNode: root });
    for (const modifierTree of modifiers) {
        formulas.push(...getAllFormulas({ modifierNode: modifierTree }));
    }
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.FullSupportedFormulaEngines.HyperFormula1, formulas, f1EngineMode);
    // Parse all formulas to find concentrationLimitWaivers variables that need to be cleared
    // between receivables to prevent stale values from previous receivables persisting
    const concentrationLimitWaiverVariables = new Set();
    for (const formula of formulas) {
        // Skip undefined or empty formulas
        if (!formula || formula.trim() === '') {
            continue;
        }
        const { nonRangeVariables } = (0, formula_engine_1.getFormulaComposition)(formula);
        for (const variable of nonRangeVariables) {
            if (variable.startsWith('concentrationLimitWaivers.')) {
                concentrationLimitWaiverVariables.add(variable);
            }
        }
    }
    const initializeEngine = (_dataSet) => {
        // Placeholder to comply with typing. Nothing to initialize currently for base HyperEngine
    };
    const tearDownEngine = () => {
        // Placeholder to comply with typing. Nothing to tear down currently for base HyperEngine
    };
    const evaluateConfig = (inputField) => {
        (0, common_1.finleyAssert)(inputField, 'To evaluate Receivable Advance Rate Trees, inputField must be passed in.');
        // we want to pass subset of fields to formula engine.
        // explicitly pick and set variables to formula engine
        const sanitizedInput = sanitizeConcentrationLimitReceivable(inputField);
        // Clear concentrationLimitWaivers variables before setting new values to prevent
        // stale values from previous receivables persisting (since setVariables only updates
        // keys present in the input, it doesn't clear keys that are missing)
        if (concentrationLimitWaiverVariables.size > 0) {
            const clearedWaivers = {};
            for (const variable of concentrationLimitWaiverVariables) {
                clearedWaivers[variable] = 0;
            }
            formulaEngine.setVariables(clearedWaivers);
        }
        (0, common_1.finleyAssert)(formulaEngine.evaluateBoolean(root.isMatch.formula), 'Root isMatch formula must be "TRUE"');
        formulaEngine.setVariables(sanitizedInput, {
            flatten: true,
            fixupStringVariables: stringFields,
        });
        const { advanceRate, path: advanceRatePathList, modifierPath: modifierPathList, } = getAdvanceRate({
            advanceRateNode: root,
            modifiers,
            variables: sanitizedInput,
            formulaEngine,
            stringFields,
        });
        const advanceRatePath = advanceRatePathList.join('#');
        const modifierPath = modifierPathList.join('>');
        const path = modifierPath.length > 0
            ? `${advanceRatePath}>${modifierPath}`
            : advanceRatePath;
        return { advanceRate, path };
    };
    return { initializeEngine, tearDownEngine, evaluateConfig };
};
const advanceRateFormulaEngineEvaluator = (formulaString, f1EngineMode) => {
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.FullSupportedFormulaEngines.HyperFormula1, [formulaString], f1EngineMode);
    // Parse formula to find concentrationLimitWaivers variables that need to be cleared
    // between receivables to prevent stale values from previous receivables persisting
    const { nonRangeVariables } = (0, formula_engine_1.getFormulaComposition)(formulaString);
    const concentrationLimitWaiverVariables = nonRangeVariables.filter((variable) => variable.startsWith('concentrationLimitWaivers.'));
    const initializeEngine = (_dataSet) => {
        // Placeholder to comply with typing. Nothing to initialize currently for base HyperEngine
    };
    const tearDownEngine = () => {
        // Placeholder to comply with typing. Nothing to tear down currently for base HyperEngine
    };
    const evaluateConfig = (inputField) => {
        // It's possible to evaluate without any input fields as we sometimes set formulas simply
        // as numbers (e.g., =0.875). If variables are missing, underlying HyperFormula will throw error
        if (inputField) {
            // we want to pass subset of fields to formula engine.
            const sanitizedInput = sanitizeConcentrationLimitReceivable(inputField);
            // Clear concentrationLimitWaivers variables before setting new values to prevent
            // stale values from previous receivables persisting (since setVariables only updates
            // keys present in the input, it doesn't clear keys that are missing)
            if (concentrationLimitWaiverVariables.length > 0) {
                const clearedWaivers = {};
                for (const variable of concentrationLimitWaiverVariables) {
                    clearedWaivers[variable] = 0;
                }
                formulaEngine.setVariables(clearedWaivers);
            }
            // explicitly pick and set variables to formula engine
            formulaEngine.setVariables(sanitizedInput, {
                flatten: true,
                // we allow special variable here for advanceRate formulas that contain ambiguousKey
                // concentrationLimitWaivers.<concentrationLimitPropertyKey>.<reason>
                // this is fine since we don't need to decompose this variable set
                ambiguousKey: true,
            });
        }
        const advanceRate = formulaEngine.evaluateNumber(formulaString);
        return { advanceRate, path: 'default' };
    };
    return { initializeEngine, tearDownEngine, evaluateConfig };
};
const advanceRateExcelEngineEvaluator = (formulaString, f1EngineMode) => {
    let excelEngine = null;
    const initializeEngine = (dataSet) => {
        const santiziedInputFields = dataSet.map((input) => sanitizeConcentrationLimitReceivable(input));
        excelEngine = (0, formula_engine_1.initializeExcelEngine)({
            data: santiziedInputFields,
            formulas: [formulaString],
            f1EngineMode,
        });
    };
    const tearDownEngine = () => {
        (0, common_1.finleyAssert)(excelEngine, 'ExcelEngine is not set. Make sure to initialize first before tearing down');
        excelEngine.clearSheet();
    };
    const evaluateConfig = (inputField) => {
        (0, common_1.finleyAssert)(excelEngine, 'ExcelEngine is not set. Make sure to initialize first before evaluating');
        let setVariableNames = [];
        if (inputField) {
            const sanitizedTargetInput = sanitizeConcentrationLimitReceivable(inputField);
            setVariableNames = excelEngine.setVariables(sanitizedTargetInput);
        }
        const advanceRate = excelEngine.evaluateNumber(formulaString);
        excelEngine.clearVariables(setVariableNames);
        return { advanceRate, path: 'default' };
    };
    return { initializeEngine, tearDownEngine, evaluateConfig };
};
const createReceivableAdvanceRateEvaluator = (receivableAdvanceRateConfig, stringFields, f1EngineMode) => {
    switch (receivableAdvanceRateConfig.type) {
        case 'formula': {
            const { engineVersion, formula } = receivableAdvanceRateConfig.config;
            switch (engineVersion) {
                case finley_types_1.FormulaEngine.FullSupportedFormulaEngines.HyperFormula1: {
                    return advanceRateFormulaEngineEvaluator(formula, f1EngineMode);
                }
                case finley_types_1.FormulaEngine.FullSupportedFormulaEngines.ExcelEngine1: {
                    return advanceRateExcelEngineEvaluator(formula, f1EngineMode);
                }
                default:
                    return (0, common_1.finleyAssertUnreachable)(engineVersion);
            }
        }
        case 'tree': {
            const { root, modifiers } = receivableAdvanceRateConfig;
            return advanceRateTreeEvaluator(root, modifiers, stringFields, f1EngineMode);
        }
        default:
            return (0, common_1.finleyAssertUnreachable)(receivableAdvanceRateConfig);
    }
};
exports.createReceivableAdvanceRateEvaluator = createReceivableAdvanceRateEvaluator;
const createReceivableAdvanceRateMiscAvailableFieldProposals = () => {
    return Object.values(advanceRateValidations_1.ReceivableAdvanceRateMiscAvailableFieldConfigs).map(({ label, description }) => ({
        label,
        documentation: description,
        insertText: label,
    }));
};
exports.createReceivableAdvanceRateMiscAvailableFieldProposals = createReceivableAdvanceRateMiscAvailableFieldProposals;
//# sourceMappingURL=advanceRateEvaluator.js.map